import axios from 'axios';
import {toast} from 'react-toastify';
import API_CONFIG from '../config';
import storage from '../utils/storage';
import {parseApiError} from '../utils/errorHandler';
import {setupTokenRefreshInterceptor} from './tokenRefreshInterceptor';
import Logger from '../utils/logger';

/**
 * Instance Axios centralisée et unique pour toutes les requêtes API
 * Gère automatiquement :
 * - Injection du token JWT
 * - Refresh automatique du token avant expiration
 * - Gestion des erreurs globales
 * - Redirection en cas de session expirée
 */
const client = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: API_CONFIG.DEFAULT_HEADERS,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
});

// ============ INTERCEPTEUR DE REQUÊTE ============
// Ajoute le token JWT à chaque requête sortante
client.interceptors.request.use((config) => {
  const token = storage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // ✅ Nettoyer le header si pas de token
    delete config.headers.Authorization;
  }
  return config;
});

// ============ INTERCEPTEUR DE RÉPONSE ============
// Gère les erreurs globales et la logique de déconnexion
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const isActivationEndpoint = error.config?.url?.includes('/activate');

      switch (status) {
        case 400: // Bad Request
          // Erreurs de validation : on parse et on renvoie au composant sans toast global
          error.parsed = parseApiError(error);
          return Promise.reject(error);

        case 401: // Unauthorized
          // Ne pas rediriger automatiquement pour l'endpoint d'activation
          if (isActivationEndpoint) {
            return Promise.reject(error);
          }

          // Si l'erreur 401 vient de la page de connexion, c'est une erreur d'identifiants.
          // Sinon, c'est probablement un token expiré.
          if (error.config.url.includes(API_CONFIG.ENDPOINTS.AUTH.LOGIN)) {
            toast.error(data?.detail || 'Identifiants incorrects. Veuillez réessayer.');
          } else {
            toast.error('Session expirée. Veuillez vous reconnecter.');
            // ✅ Utiliser storage.clearAll() qui envoie un signal de déconnexion
            storage.clearAll();
            // ✅ Nettoyer le header Authorization
            delete client.defaults.headers.common['Authorization'];
            // Délai pour que le toast soit visible avant la redirection
            setTimeout(() => {
              window.location.href = '/';
            }, 2000);
          }
          break;

        case 403: // Forbidden
          toast.error("Vous n'avez pas les permissions nécessaires pour cette action.");
          break;

        case 404: // Not Found
          toast.error("La ressource demandée n'a pas été trouvée.");
          break;

        case 429: // Too Many Requests
          toast.error('Trop de requêtes. Veuillez attendre avant de réessayer.');
          break;

        case 500: // Internal Server Error
          toast.error('Erreur interne du serveur. Veuillez réessayer plus tard.');
          break;

        default:
          toast.error(`Une erreur inattendue est survenue (${status}).`);
      }
    } else if (error.request) {
      toast.error('Le serveur ne répond pas. Vérifiez votre connexion réseau.');
    }
    return Promise.reject(error);
  }
);

// Configurer le refresh automatique des tokens
setupTokenRefreshInterceptor(client);

Logger.log('[API Client] Client Axios configuré avec refresh automatique des tokens');

export default client;
